import React from 'react';
import styled, { css } from 'styled-components';

import { spacing } from 'ui/theme/utils';
import { IconButton } from 'ui/components/shared';
import { useOnClickOutside, useChildrenWithProps } from 'hooks';
import { noop } from 'helpers/event';

const DropDownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  ${(props) => spacing(props)}
  width: 100%;
  justify-content: space-between;
`;

const DropDownListContainer = styled.div`
  ${({ fixed }) =>
    fixed
      ? css`
          position: fixed;
          top: ${({ position: { top = 0 } }) => `${top}px`};
          left: ${({ position: { left = 0 } }) => `${left}px`};
        `
      : css`
          position: absolute;
          top: 100%;
          right: -5px;
        `}
  z-index: 100;
  margin-top: ${({ mt = '3px' }) => mt};
`;

const DropDownList = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: ${({ padding = '12px 15px' }) => padding};
  min-width: ${({ width }) => `${width}px`};
  border-radius: 10px;
  background-color: #fff;
  box-shadow: ${({ boxShadow = 'default' }) =>
    ({
      thin: '0px 1px 8px rgba(0, 0, 0, 0.16)',
      default: '0px 0px 16px rgba(0, 0, 0, 0.16)',
    }[boxShadow] || boxShadow)};
  box-sizing: border-box;
  cursor: auto;
`;

const ListItem = styled.li`
  font-size: 14px;
  letter-spacing: 0.04em;
  text-align: left;
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  line-height: 20px;
  font-weight: 400;
  list-style: none;
  cursor: pointer;
  &:not(:last-child) {
    margin-bottom: ${({ gap = '12px' }) => gap};
  }
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: ${({ disabledCursor = 'not-allowed' }) => disabledCursor};
    `}
`;

const DefaultIcon = ({ color = '#aaa', width = 18, height = 4 }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 18 4">
      <g id="Groupe_336" data-name="Groupe 336" transform="translate(-700 -334)">
        <circle
          id="Ellipse_72"
          data-name="Ellipse 72"
          cx="2"
          cy="2"
          r="2"
          transform="translate(700 334)"
          fill={color}
        />
        <circle
          id="Ellipse_73"
          data-name="Ellipse 73"
          cx="2"
          cy="2"
          r="2"
          transform="translate(707 334)"
          fill={color}
        />
        <circle
          id="Ellipse_74"
          data-name="Ellipse 74"
          cx="2"
          cy="2"
          r="2"
          transform="translate(714 334)"
          fill={color}
        />
      </g>
    </svg>
  );
};

const Label = styled.span`
  ${({ onClick }) => onClick && `cursor: pointer;`}
  ${(props) => spacing(props)}
`;

const DropDownMenu = React.forwardRef(
  (
    {
      children,
      label,
      labelOnly,
      icon: Icon = DefaultIcon,
      width = 150, // px
      padding,
      gap,
      fixed = true,
      fixedLeftSpacing = 5, // px
      iconButtonProps,
      listContainerProps,
      listProps,
      listDirection,
      ...rest
    },
    forwardedRef
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [position, setPosition] = React.useState({ top: 0, left: 0 });
    const dropdownRef = React.useRef();
    const ref = React.useRef();
    const childrenWithProps = useChildrenWithProps(children, (child) => ({
      onClick: (event) => {
        setIsOpen(false);
        const { onClick, disabled } = child.props;
        if (onClick && !disabled) {
          onClick(event);
        }
      },
      gap,
    }));
    useOnClickOutside(ref, () => setIsOpen(false));

    const handleClick = (event) => {
      event.stopPropagation();
      setIsOpen(!isOpen);
    };

    React.useEffect(() => {
      if (fixed && isOpen) {
        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        const dropdownListRect = ref.current.getBoundingClientRect();
        const topPos =
          listDirection === 'top'
            ? dropdownRect.top - (dropdownRect.height + dropdownListRect.height + 10)
            : dropdownRect.top + dropdownRect.height;
        const pos = {
          top: topPos,
          left: dropdownRect.right - width + fixedLeftSpacing,
        };
        if (pos.top < 0) pos.top = 10;
        if (pos.left < 0) pos.left = 10;
        setPosition(pos);
      }
    }, [isOpen, fixed]);

    // allow us to call handleClick from parent
    React.useImperativeHandle(forwardedRef, () => ({
      handleClick,
    }));

    return (
      <DropDownContainer ref={dropdownRef} {...rest}>
        {label && labelOnly ? (
          <Label onClick={handleClick}>{label}</Label>
        ) : (
          <>
            {label && <Label mr="xxs">{label}</Label>}
            <IconButton
              variant="transparent"
              size="18px"
              {...iconButtonProps} // keep here to avoid overriding below props
              icon={({ color, colorActive = '#3591fc', ...rest }) => (
                <Icon
                  color={isOpen && colorActive ? colorActive : color}
                  isOpen={isOpen}
                  {...rest}
                />
              )}
              onClick={handleClick}
            />
          </>
        )}
        {isOpen && (
          <DropDownListContainer
            ref={ref}
            fixed={fixed}
            position={position}
            {...listContainerProps}
          >
            <DropDownList width={width} padding={padding} {...listProps} onClick={noop}>
              {childrenWithProps}
            </DropDownList>
          </DropDownListContainer>
        )}
      </DropDownContainer>
    );
  }
);

export { DropDownMenu, ListItem as DropDownMenuItem };
